import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useT } from '../i18n/I18nProvider';
import { Banner } from './states';

interface Finding {
  code: string;
  status: 'ok' | 'warning' | 'blocker';
  detail: string;
}

interface Readiness {
  ready: boolean;
  findings: Finding[];
}

const STATUS_CHIP: Record<string, string> = {
  ok: 'chip--success',
  warning: 'chip--warning',
  blocker: 'chip--critical',
};

/**
 * Runs the pre-upgrade checks for the tenant and shows the evidence: whether it is safe to
 * upgrade now, and what to settle first. Tenant admins only.
 */
export function UpgradeReadinessPanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const [result, setResult] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!can('tenant:admin')) return <></>;

  const run = (): void => {
    setError(null);
    setResult(null);
    setBusy(true);
    void (async () => {
      try {
        setResult(await api.get<Readiness>('/operations/upgrade-readiness'));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="card">
      <h2>{t('upgradeReadiness.title')}</h2>
      <p>{t('upgradeReadiness.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}

      {result ? (
        <>
          <Banner variant={result.ready ? 'success' : 'error'}>
            {result.ready ? t('upgradeReadiness.ready') : t('upgradeReadiness.notReady')}
          </Banner>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('upgradeReadiness.status')}</th>
                  <th>{t('upgradeReadiness.finding')}</th>
                </tr>
              </thead>
              <tbody>
                {result.findings.map((finding) => (
                  <tr key={finding.code}>
                    <td>
                      <span className={`chip ${STATUS_CHIP[finding.status] ?? ''}`}>
                        {finding.status}
                      </span>
                    </td>
                    <td>{finding.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <button type="button" className="btn" disabled={busy} onClick={run}>
        {busy ? t('upgradeReadiness.checking') : t('upgradeReadiness.check')}
      </button>
    </div>
  );
}
